import React, { useState, useEffect } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import routes from '../../router/routes';
import { CustomLink } from 'components/CustomLink';
import { useTranslation } from 'react-i18next';
import useApiContext from '../../hooks/useApiContext';
import useRepo from '../../hooks/useRepo';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
} from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PeopleIcon from '@mui/icons-material/People';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import GridViewIcon from '@mui/icons-material/GridView';
import FolderIcon from '@mui/icons-material/Folder';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CheckIcon from '@mui/icons-material/Check';
import DescriptionIcon from '@mui/icons-material/Description';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import LanguageIcon from '@mui/icons-material/Language';
import { eRoles } from 'interfaces/Roles';

const SIDEBAR_WIDTH = 230;
// Height of the app's sticky top header (#app-header Toolbar minHeight), so the
// fixed sidebar starts below it instead of being hidden underneath.
const HEADER_HEIGHT = 70;

interface NavItem {
  label: string;
  icon: React.ReactNode;
  path: string;
  matchFn: (p: string) => boolean;
  group?: number;
}

// Builds "{slug}-{year}" without duplicating the year if the slug already ends with it
const withYear = (
  slug?: string | null,
  year?: string | number | null
): string => {
  if (!slug) return '';
  if (!year) return slug;
  return slug.endsWith(`-${year}`) ? slug : `${slug}-${year}`;
};

export function ExhibitorListHeader(): JSX.Element {
  const { pathname } = useLocation();
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { eventRepository, userRepository } = useApiContext();
  const user = userRepository.getUser();

  const { response, loading } = useRepo(
    async () =>
      eventId !== undefined
        ? await eventRepository.getEventById(eventId).then((e) => e)
        : '',
    [pathname]
  );

  const [name, setName] = useState<string>();
  const [year, setYear] = useState<any>();
  const [date, setDate] = useState<string>();
  const [slug, setSlug] = useState<string>();

  useEffect(() => {
    if (eventId && !loading && response) {
      setName(response.name);
      setYear(response.year);
      setSlug(response.slug);
      const dateStr = response.date;
      if (dateStr) {
        const [yr, month, day] = dateStr.split('-');
        setDate(`${day}/${month}/${yr}`);
      }
    }
  }, [loading, response]);

  const base = routes.home2.path + `events/${eventId ?? ''}`;

  // Moderators only get the event-specific editors for events they created.
  // Missing event/creator info is treated as "not the creator".
  const eventCreator = response
    ? (response as { creator?: { email?: string } }).creator ?? null
    : null;
  const canAccessEventEditors =
    user?.role !== eRoles.MODERATOR ||
    (!!user?.email &&
      !!eventCreator?.email &&
      user.email === eventCreator.email);

  const navItems: NavItem[] = [
    {
      label: t('eventList:events'),
      icon: <CalendarMonthIcon fontSize="small" />,
      path: `${base}/edit`,
      matchFn: (p) => p.includes('edit') && !p.includes('editor'),
      group: 1,
    },
    {
      label: t('eventList:exhibitor'),
      icon: <PeopleIcon fontSize="small" />,
      path: base,
      matchFn: (p) =>
        !p.includes('edit') &&
        !p.includes('editor') &&
        !p.includes('floormap') &&
        !p.includes('offers') &&
        !p.includes('form') &&
        !p.includes('exhibitor-list') &&
        !p.includes('connector-admin'),
      group: 1,
    },
    ...(user?.role === eRoles.ADMIN
      ? [
          {
            label: t('event:connector'),
            icon: <AdminPanelSettingsIcon fontSize="small" />,
            path: `${base}/connector-admin`,
            matchFn: (p: string) => p.includes('connector-admin'),
            group: 1,
          },
        ]
      : []),
    {
      label: t('event:add_exhibitors'),
      icon: <PersonAddAltIcon fontSize="small" />,
      path: `${base}/exhibitor-list`,
      matchFn: (p) => p.includes('exhibitor-list'),
      group: 1,
    },
    ...(canAccessEventEditors
      ? [
          {
            label: t('eventList:offerEditor'),
            icon: <FolderIcon fontSize="small" />,
            path: `${base}/editor-3`,
            matchFn: (p: string) => p.endsWith('editor-3'),
            group: 2,
          },
          {
            label: t('eventList:websiteEditor'),
            icon: <LanguageIcon fontSize="small" />,
            path: `${base}/editor-4`,
            matchFn: (p: string) => p.endsWith('editor-4'),
            group: 2,
          },
          {
            label: t('eventList:floorMap'),
            icon: <GridViewIcon fontSize="small" />,
            path: `${base}/floormap`,
            matchFn: (p: string) => p.includes('floormap'),
            group: 2,
          },
          {
            label: t('eventList:pdfEditor'),
            icon: <PictureAsPdfIcon fontSize="small" />,
            path: `${base}/editor-2`,
            matchFn: (p: string) => p.endsWith('editor-2'),
            group: 2,
          },
          {
            label: t('eventList:offers'),
            icon: <LocalOfferIcon fontSize="small" />,
            path: `${base}/offers`,
            matchFn: (p: string) => p.includes('offers'),
            group: 2,
          },
        ]
      : []),
    ...(slug
      ? [
          {
            label: t('eventList:exhibitorsWebsite'),
            icon: <LanguageIcon fontSize="small" />,
            path: `${routes.home2.path}preview/events/${withYear(
              slug,
              year
            )}/exhibitors`,
            matchFn: (p: string) => p.includes('/preview/events/'),
            group: 3,
          },
        ]
      : []),
  ];

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#F9FAFB' }}>
      {/* Sidebar */}
      <Box
        component="nav"
        sx={{
          width: SIDEBAR_WIDTH,
          flexShrink: 0,
          bgcolor: '#ffffff',
          borderRight: '1px solid #eee',
          display: 'flex',
          flexDirection: 'column',
          position: 'fixed',
          top: HEADER_HEIGHT,
          left: 0,
          height: `calc(100vh - ${HEADER_HEIGHT}px)`,
          overflowY: 'hidden',
        }}
      >
        {/* Event header — pink calendar icon + event name */}
        <Box sx={{ px: 2, pt: 2 }}>
          <Box
            role="link"
            tabIndex={0}
            onClick={() => navigate(routes.admEventList.path)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') navigate(routes.admEventList.path);
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              cursor: 'pointer',
            }}
          >
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: '8px',
                bgcolor: '#eff6ff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <CalendarMonthIcon sx={{ fontSize: 18, color: '#3b82f6' }} />
            </Box>
            <Typography
              sx={{ fontSize: '13px', fontWeight: 600, color: '#111' }}
            >
              {t('header:events')}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ px: 2, py: 2, borderBottom: '1px solid #f0f0f0' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: '8px',
                bgcolor: '#fee2e2',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <CalendarMonthIcon sx={{ fontSize: 18, color: '#ef4444' }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              {name && (
                <Typography
                  sx={{
                    fontSize: '13px',
                    fontWeight: 600,
                    color: '#111',
                    lineHeight: 1.3,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {name} {year}
                </Typography>
              )}
              {date && (
                <Typography sx={{ fontSize: '11px', color: '#aaa', mt: 0.25 }}>
                  {date}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>

        {/* Nav items */}
        <List disablePadding sx={{ py: 1.5, flex: 1 }}>
          {navItems.map((item, index) => {
            const isActive = item.matchFn(pathname);
            const prevGroup =
              index > 0 ? navItems[index - 1].group : item.group;
            const showDivider = index > 0 && item.group !== prevGroup;

            return (
              <React.Fragment key={item.label}>
                {showDivider && (
                  <Divider sx={{ mx: 2, my: 1, borderColor: '#f0f0f0' }} />
                )}
                <ListItem disablePadding>
                  <ListItemButton
                    component={CustomLink as any}
                    to={item.path}
                    color="black"
                    textDecoration="none"
                    sx={{
                      px: 2,
                      py: 0.875,
                      mx: 1,
                      mb: 0.25,
                      borderRadius: '8px',
                      backgroundColor: isActive ? '#f5f5f5' : 'transparent',
                      '&:hover': {
                        backgroundColor: isActive ? '#eeeeee' : '#f9f9f9',
                      },
                      transition: 'background-color 0.15s',
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 30,
                        color: isActive ? '#111' : '#bbb',
                      }}
                    >
                      {isActive ? (
                        <CheckIcon
                          sx={{
                            fontSize: 17,
                            fontWeight: 700,
                            strokeWidth: 2,
                          }}
                        />
                      ) : (
                        item.icon
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        fontSize: '13px',
                        fontWeight: isActive ? 600 : 400,
                        color: isActive ? '#111' : '#666',
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              </React.Fragment>
            );
          })}
        </List>

        {/* Back to event list */}
        <Box sx={{ p: 2, borderTop: '1px solid #f0f0f0' }}>
          <Box
            component={CustomLink as any}
            to={routes.admEventList.path}
            color="black"
            textDecoration="none"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              fontSize: '12px',
              color: '#aaa',
              '&:hover': { color: '#555' },
              transition: 'color 0.15s',
            }}
          >
            ← Tous les événements
          </Box>
        </Box>
      </Box>

      {/* Main content */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          overflowX: 'auto',
          marginLeft: `${SIDEBAR_WIDTH}px`,
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
}
